import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { MediaType } from "@/mocks/movies";
import { searchMedia } from "@/lib/media";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import SearchBar from "@/components/feature/SearchBar";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 8;

const FILTERS: { value: MediaType | "all"; label: string; icon: string }[] = [
  { value: "all", label: "全部", icon: "ri-apps-2-line" },
  { value: "movie", label: "电影", icon: "ri-film-line" },
  { value: "tv", label: "电视剧", icon: "ri-tv-2-line" },
];

export default function Search() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get("q") ?? "").trim();
  const [filterType, setFilterType] = useState<MediaType | "all">("all");
  const [page, setPage] = useState(1);

  useDocumentMeta(
    keyword ? `搜索“${keyword}” - 最近中文字幕` : "搜索 - 最近中文字幕",
    keyword
      ? `在最近中文字幕中搜索“${keyword}”的相关影视资源与中文字幕信息，涵盖电影与电视剧类别。`
      : "搜索最近中文字幕站内的影视资源与中文字幕信息。",
    "中文字幕搜索,影视资源搜索,中文字幕电影,中文字幕电视剧"
  );

  const results = useMemo(
    () => searchMedia(keyword, filterType),
    [keyword, filterType]
  );

  const totalPages = Math.ceil(results.length / PAGE_SIZE) || 1;

  useEffect(() => {
    setPage(1);
  }, [keyword, filterType]);

  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="animate-fade-up">
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6 md:py-8">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-400">
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">搜索结果</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {keyword ? `搜索“${keyword}”` : "搜索影视资源"}
          </h1>
          <div className="mt-4 max-w-2xl">
            <SearchBar defaultValue={keyword} size="lg" />
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6 md:py-8">
        {keyword ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-500">
                共找到{" "}
                <strong className="font-medium text-accent-600">{results.length}</strong>{" "}
                条与“{keyword}”相关的中文字幕资源
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {FILTERS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFilterType(option.value)}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                      filterType === option.value
                        ? "bg-primary-500 font-medium text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    <i className={option.icon}></i>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="mt-6 flex flex-col gap-3 md:gap-4">
                  {pageItems.map((item) => (
                    <MovieCard key={item.id} item={item} variant="row" />
                  ))}
                </div>
                <div className="mt-8">
                  <Pagination current={page} total={totalPages} onChange={setPage} />
                </div>
              </>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
                  <i className="ri-search-eye-line"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                  没有找到相关内容
                </h3>
                <p className="mt-2 max-w-sm text-sm text-foreground-500">
                  换个关键词试试，或浏览分类专区发现更多带中文字幕的影视作品。
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/"
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-home-5-line"></i>
                    返回首页
                  </Link>
                  <Link
                    to="/category/movie"
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-film-line"></i>
                    浏览电影专区
                  </Link>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
              <i className="ri-search-line"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              输入关键词开始搜索
            </h3>
            <p className="mt-2 max-w-sm text-sm text-foreground-500">
              支持按片名、类型、主演、地区与标签检索带中文字幕的影视资源。
            </p>
          </div>
        )}
      </div>
    </div>
  );
}